<template>
    <div class="home">
        <v-container fluid grid-list-sm>
            <v-row>
                <v-col cols="12" sm="9">
                    <h3 class="display-1 primary--text">Super Search</h3>
                </v-col>
                <v-col cols="12" sm="9">
                    <v-text-field v-model="search" label="Search Customers, Parts, Bookings and Estimates" />
                    <v-btn :loading="isLoading" :disabled="isLoading" color="primary" large @click="doSearch">Search</v-btn>
                    <v-btn class="ml-4" color="secondary" large @click="resetSearch">Reset</v-btn>
                </v-col>
                <v-col v-if="results.customers.length > 0" cols="12" sm="12">
                    <h3 class="primary--text">Customers</h3>
                    <v-data-table :loading="isLoading" :headers="cHeaders" :items="results.customers" class="elevation-1 mt-5">
                        <template v-slot:item.vrm="{item}">
                            <span v-if="item.vrm" class="vrm"> {{ item.vrm }} </span><span v-else>N/A</span>
                        </template>
                        <template v-slot:item.actions="{item}">
                            <v-icon large color="info" class="mr-2" @click="editCustomer(item)">mdi-pencil</v-icon>
                            <v-icon large color="error" @click="deleteCustomer(item)">mdi-delete</v-icon>
                        </template>
                    </v-data-table>
                </v-col>
                <v-col v-if="results.parts.length > 0" cols="12" sm="12">
                    <h3 class="primary--text">Parts</h3>
                    <v-data-table :headers="pHeaders" :items="results.parts" class="elevation-1 mt-5">
                        <template v-slot:item.customer.vrm="{item}">
                            <span v-if="item.customer.vrm" class="vrm"> {{ item.customer.vrm }} </span><span v-else>N/A</span>
                        </template>
                        <template v-slot:item.bookingDate="{item}">
                            <span> {{ item.bookingDate | moment('DD/MM/YYYY') }} </span>
                        </template>
                        <template v-slot:item.orderDate="{item}">
                            <span> {{ item.orderDate | moment('DD/MM/YYYY') }} </span>
                        </template>
                        <template v-slot:item.price="{item}">
                            <span v-if="item.price"> £{{ item.price.toFixed(2) }} </span>
                        </template>
                        <template v-slot:item.isOrdered="{item}">
                            <td><v-checkbox @change="editPart(item)" class="pt-3" v-model="item.isOrdered">{{ item.isOrdered }}</v-checkbox></td>
                        </template>
                        <template v-slot:item.isDelivered="{item}">
                            <td><v-checkbox @change="editPart(item)" class="pt-3" v-model="item.isDelivered">{{ item.isDelivered }}</v-checkbox></td>
                        </template>
                        <template v-slot:item.actions="{item}">
                            <v-icon :disabled="!item.booking" large color="primary" @click="manageBooking(item)">mdi-calendar</v-icon>
                            <v-icon large color="error" @click="deletePart(item)">mdi-delete</v-icon>
                        </template>
                    </v-data-table>
                </v-col>
                <v-col v-if="results.bookings.length > 0" cols="12" sm="12">
                    <h3 class="primary--text">Bookings</h3>
                    <v-data-iterator :items="results.bookings" hide-default-footer :options="{itemsPerPage: 30}">
                        <template v-slot:default="props">
                            <v-row>
                                <v-col v-for="(item, index) in props.items" cols="12" sm="12" style="max-width: 350px" :key="index">
                                    <booking-card-day :item="item" />
                                </v-col>
                            </v-row>
                        </template>
                    </v-data-iterator>
                </v-col>
                <v-col v-if="results.estimates.length > 0" cols="12" sm="12">
                    <h3 class="primary--text">Estimates</h3>
                    <v-data-table :headers="eHeaders" :items="results.estimates" class="elevation-1 mt-5">
                        <template v-slot:item.customer.vrm="{item}">
                            <span v-if="item.customer.vrm" class="vrm"> {{ item.customer.vrm }} </span><span v-else>N/A</span>
                        </template>
                        <template v-slot:item.estimateDate="{item}">
                            <span> {{ item.estimateDate | moment('DD/MM/YYYY') }} </span>
                        </template>
                        <template v-slot:item.estimateExpiry="{item}">
                            <span> {{ item.estimateExpiry | moment('DD/MM/YYYY') }} </span>
                        </template>
                        <template v-slot:item.total="{item}">
                            <span> £{{ item.total | amount }} </span>
                        </template>
                        <template v-slot:item.actions="{item}">
                            <v-btn class="mr-2"  :loading="isEmailing" :disabled="isEmailing" icon>
                                <v-icon large style="cursor: pointer" color="primary" @click="emailEstimate(item)">mdi-mail</v-icon>
                            </v-btn>
                            <v-btn class="mr-2"  :loading="isGenerating" :disabled="isGenerating" icon>
                                <v-icon large style="cursor: pointer" color="success" @click="printEstimate(item)">mdi-printer</v-icon>
                            </v-btn>
                            <v-icon large style="cursor: pointer" color="primary" class="mr-2" @click="viewEstimate(item)">mdi-magnify</v-icon>
                            <v-icon large style="cursor: pointer" color="secondary" class="mr-2" @click="createBooking(item)">mdi-calendar</v-icon>
                            <v-icon large color="info" class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
                            <v-icon large color="error" @click="deleteItem(item)">mdi-delete</v-icon>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import {SearchHttp} from "../http/search";
    import {PartsHttp} from "../http/parts";
    import {EventBus} from "../EventBus";
    import BookingCardDay from "../components/cards/BookingCardDay";
    import {EstimatesHttp} from "../http/estimates";

    export default {
        name: "Search",
        components: {BookingCardDay},
        data() {
            return {
                search: '',
                results: {
                    customers: [],
                    parts: [],
                    estimates: [],
                    bookings: []
                },
                isGenerating: false,
                isEmailing: false,
                isLoading: false,
                cHeaders: [
                    { text: 'Name', value: 'name' },
                    { text: 'Email', value: 'email' },
                    { text: 'Phone', value: 'phone' },
                    { text: 'Registration', value: 'vrm' },
                    { text: 'Actions', value: 'actions', sortable: false }
                ],
                pHeaders: [
                    { text: 'Part Name', value: 'name' },
                    { text: 'Customer', value: 'customer.name' },
                    { text: 'Make', value: 'customer.make' },
                    { text: 'VRM', value: 'customer.vrm' },
                    { text: 'Price', value: 'price' },
                    { text: 'Booking Date', value: 'bookingDate' },
                    { text: 'Ordered Date', value: 'orderDate' },
                    { text: 'Is Ordered', value: 'isOrdered' },
                    { text: 'Is Delivered', value: 'isDelivered' },
                    { text: 'Actions', value: 'actions', sortable: false }
                ],
                eHeaders: [
                    { text: 'Customer', value: 'customer.name' },
                    { text: 'Make', value: 'customer.make' },
                    { text: 'Estimate Date', value: 'estimateDate' },
                    { text: 'Estimate Expires', value: 'estimateExpiry' },
                    { text: 'VRM', value: 'customer.vrm' },
                    { text: 'Total', value: 'total' },
                    { text: 'Actions', value: 'actions' }
                ],
            }
        },
        methods: {
            resetSearch() {
                this.results = {
                    customers: [],
                    parts: [],
                    estimates: [],
                    bookings: []
                };
                this.search = '';
            },
            async doSearch() {
                if (this.search.length > 0) {
                    this.isLoading = true;
                    this.results = await SearchHttp.doSearch(this.search);
                    this.isLoading = false;
                }
            },
            async editCustomer(item) {
                await this.$store.dispatch('customers/setActiveCustomer', item);
                await this.$store.dispatch('customers/setManageCustomerDialog', true);
            },
            async deleteCustomer(item) {
                await this.$store.dispatch('customers/setActiveCustomer', item);
                await this.$store.dispatch('customers/setManageDeleteCustomerDialog', true);
            },
            async editPart(item) {
                this.isLoading = true;
                try {
                    await PartsHttp.updatePart(item._id, item);
                    EventBus.$emit('message', {message: 'Part Updated Successfully', type: 'success'});
                    await this.$store.dispatch('parts/setParts');
                } catch (e) {
                    EventBus.$emit('message', {message: e, type: 'error'});
                } finally {
                    this.isLoading = false;
                }
            },
            async deletePart(part) {
                this.isLoading = true;
                try {
                    await PartsHttp.deletePart(part._id);
                    EventBus.$emit('message', {message: 'Part Deleted Successfully', type: 'success'});
                    await this.$store.dispatch('parts/setParts');
                } catch (e) {
                    EventBus.$emit('message', {message: e, type: 'error'});
                } finally {
                    this.isLoading = false;
                }
            },
            async manageBooking(item) {
                await this.$store.dispatch('bookings/setActiveBooking', item.booking);
                await this.$store.dispatch('bookings/setManageBookingDialog', true);
            },
            viewEstimate(item){
                this.$router.push(`/estimate/${item._id}`);
            },
            async printEstimate(item){
                this.isGenerating = true;
                const blob = await EstimatesHttp.printEstimate(item._id);
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                window.open(link.href, '_blank');
                this.isGenerating = false;
            },
            async emailEstimate(item) {
                this.isEmailing = true;
                try {
                    await EstimatesHttp.emailEstimate(item._id);
                    EventBus.$emit('message', {message: 'Email sent Successfully', type: 'success'});
                } catch (e) {
                    EventBus.$emit('message', {message: e.message, type: 'error'});
                } finally {
                    this.isEmailing = false;
                }
            },
            createBooking(item) {
                this.$store.dispatch('estimates/setActiveEstimate', item);
                const newBooking = {...item};
                newBooking.dailyTotals = [];
                delete newBooking._id;
                this.$store.dispatch('bookings/setActiveBooking', newBooking);
                this.$store.dispatch('bookings/setManageBookingDialog', true);
            },
            editItem(item) {
                this.$store.dispatch('estimates/setActiveEstimate', item);
                this.$store.dispatch('estimates/setManageEstimateDialog', true);
            },
            deleteItem(item) {
                this.$store.dispatch('estimates/setActiveEstimate', item);
                this.$store.dispatch('estimates/setDeleteEstimateDialog', true);
            }
        }
    }
</script>

<style scoped>

</style>
